import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './LabInfoHelp.module.css';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

const HELP_OPEN_EVENT = 'smarti-lab-help-open';
const MOBILE_BREAKPOINT = 640;
const VIEWPORT_PADDING = 16;
const DESKTOP_WIDTH = 360;
const DESKTOP_HEIGHT = 420;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function buildSections({ bodyIt, bulletsIt, bodyEn, bulletsEn }) {
  return [
    { key: 'it', body: bodyIt, bullets: bulletsIt, muted: false },
    { key: 'en', body: bodyEn, bullets: bulletsEn, muted: true },
  ].filter((section) => section.body || (section.bullets && section.bullets.length));
}

export default function LabInfoHelp({
  titleIt,
  bodyIt,
  bulletsIt,
  titleEn,
  bodyEn,
  bulletsEn,
  ariaLabel = 'Spiegazione / Explanation',
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const instanceId = useId();
  const popoverId = `lab-help-${instanceId.replace(/:/g, '')}`;
  const title = titleIt || titleEn || 'Help';
  const sections = buildSections({ bodyIt, bulletsIt, bodyEn, bulletsEn });

  const measurePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;

    const triggerRect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const availableWidth = viewportWidth - VIEWPORT_PADDING * 2;
    const availableHeight = viewportHeight - VIEWPORT_PADDING * 2;
    const width = Math.min(DESKTOP_WIDTH, availableWidth, panel.offsetWidth || DESKTOP_WIDTH);
    const height = Math.min(DESKTOP_HEIGHT, availableHeight, panel.offsetHeight || DESKTOP_HEIGHT);
    const gap = compact ? 10 : 12;
    const belowSpace = viewportHeight - triggerRect.bottom - VIEWPORT_PADDING;
    const aboveSpace = triggerRect.top - VIEWPORT_PADDING;
    const openBelow = belowSpace >= height || belowSpace >= aboveSpace;
    const centeredLeft = triggerRect.left + triggerRect.width / 2 - width / 2;
    const left = clamp(centeredLeft, VIEWPORT_PADDING, viewportWidth - width - VIEWPORT_PADDING);
    let top = openBelow ? triggerRect.bottom + gap : triggerRect.top - height - gap;
    top = clamp(top, VIEWPORT_PADDING, viewportHeight - height - VIEWPORT_PADDING);
    const arrowLeft = clamp(
      triggerRect.left + triggerRect.width / 2 - left - 8,
      18,
      width - 18,
    );

    setPosition({
      mode: 'desktop',
      top,
      left,
      width,
      maxHeight: height,
      placement: openBelow ? 'bottom' : 'top',
      arrowLeft,
    });
  }, [compact]);

  useEffect(() => {
    const onResize = () => {
      setMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handleOtherOpen = (event) => {
      if (event.detail?.id !== instanceId) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    const handlePointerDown = (event) => {
      const panel = panelRef.current;
      const trigger = triggerRef.current;
      if (!panel || !trigger) return;
      if (panel.contains(event.target) || trigger.contains(event.target)) return;
      setOpen(false);
    };
    const updatePosition = () => {
      if (!mobile) {
        measurePosition();
      }
    };

    window.addEventListener(HELP_OPEN_EVENT, handleOtherOpen);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener(HELP_OPEN_EVENT, handleOtherOpen);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [instanceId, measurePosition, mobile, open]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      triggerRef.current?.focus();
      return;
    }
    window.dispatchEvent(new CustomEvent(HELP_OPEN_EVENT, { detail: { id: instanceId } }));
  }, [instanceId, open]);

  useLayoutEffect(() => {
    if (!open) return;
    if (mobile) {
      setPosition({ mode: 'mobile' });
      closeRef.current?.focus();
      return;
    }
    measurePosition();
    const focusTimer = window.setTimeout(() => {
      closeRef.current?.focus();
    }, prefersReducedMotion ? 0 : 80);
    return () => window.clearTimeout(focusTimer);
  }, [measurePosition, mobile, open, prefersReducedMotion]);

  const handleToggle = () => {
    setOpen((current) => !current);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const panel = open ? (
    <div
      className={`${styles.portal} ${prefersReducedMotion ? styles.reducedMotion : ''}`}
      data-mobile={position?.mode === 'mobile' ? 'true' : 'false'}
    >
      {position?.mode === 'mobile' ? (
        <>
          <button
            type="button"
            className={styles.backdrop}
            aria-label="Chiudi spiegazione / Close explanation"
            onClick={handleClose}
          />
          <section
            ref={panelRef}
            id={popoverId}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className={`${styles.panel} ${styles.mobileSheet}`}
          >
            <div className={styles.chromeLine} />
            <div className={styles.header}>
              <h3>{title}</h3>
              <button
                ref={closeRef}
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Chiudi spiegazione / Close explanation"
              >
                ×
              </button>
            </div>
            <div className={styles.body}>
              {sections.map((section) => (
                <div
                  key={section.key}
                  className={`${styles.section} ${section.muted ? styles.sectionMuted : ''}`}
                >
                  {section.body ? <p>{section.body}</p> : null}
                  {section.bullets?.length ? (
                    <ul>
                      {section.bullets.slice(0, 2).map((bullet) => <li key={bullet}>{bullet}</li>)}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        </>
      ) : (
        <section
          ref={panelRef}
          id={popoverId}
          role="dialog"
          aria-modal="false"
          aria-label={title}
          className={`${styles.panel} ${styles.desktopPopover}`}
          style={{
            top: `${position?.top ?? VIEWPORT_PADDING}px`,
            left: `${position?.left ?? VIEWPORT_PADDING}px`,
            width: `${position?.width ?? DESKTOP_WIDTH}px`,
            maxHeight: `${position?.maxHeight ?? DESKTOP_HEIGHT}px`,
          }}
        >
          <span
            className={`${styles.arrow} ${position?.placement === 'top' ? styles.arrowTop : styles.arrowBottom}`}
            style={{ left: `${position?.arrowLeft ?? 28}px` }}
            aria-hidden="true"
          />
          <div className={styles.chromeLine} />
          <div className={styles.header}>
            <h3>{title}</h3>
            <button
              ref={closeRef}
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="Chiudi spiegazione / Close explanation"
            >
              ×
            </button>
          </div>
          <div className={styles.body}>
            {sections.map((section) => (
              <div
                key={section.key}
                className={`${styles.section} ${section.muted ? styles.sectionMuted : ''}`}
              >
                {section.body ? <p>{section.body}</p> : null}
                {section.bullets?.length ? (
                  <ul>
                    {section.bullets.slice(0, 2).map((bullet) => <li key={bullet}>{bullet}</li>)}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${compact ? styles.triggerCompact : ''}`}
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={popoverId}
      >
        ?
      </button>
      {open ? createPortal(panel, document.body) : null}
    </>
  );
}
